'use client';

import { CompareFundReturnValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  values: CompareFundReturnValues;
  onValuesChange: (field: keyof CompareFundReturnValues, value: number | CompareFundReturnValues['mode']) => void;
  translations: ToolsTranslations['compareFundReturn'];
  locale: string;
}

interface SliderFieldProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix: string;
  minLabel: string;
  maxLabel: string;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
  inputMode?: 'decimal' | 'numeric';
}

function SliderField({
  id,
  label,
  value,
  min,
  max,
  step,
  suffix,
  minLabel,
  maxLabel,
  onChange,
  formatValue,
  inputMode = 'decimal',
}: SliderFieldProps) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-3">
        <label htmlFor={`${id}-input`} className="text-gray-700 font-medium">
          {label}
        </label>
        <div className="flex items-center gap-2">
          <input
            id={`${id}-input`}
            type="text"
            inputMode={inputMode}
            value={formatValue ? formatValue(value) : String(value)}
            onChange={(e) => {
              const parsed = parseFloat(e.target.value.replace(/\s/g, '').replace(',', '.'));
              if (!isNaN(parsed) && parsed >= 0) onChange(parsed);
            }}
            className="w-20 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
            aria-label={label}
          />
          <span className="text-gray-800 font-semibold">{suffix}</span>
        </div>
      </div>
      <input
        id={`${id}-slider`}
        type="range"
        min={min}
        max={max}
        step={step}
        value={Math.min(max, Math.max(min, value))}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 cursor-pointer"
        aria-label={`${label} slider`}
      />
      <div className="flex justify-between text-xs text-gray-500">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}

export default function CalculatorInputs({ values, onValuesChange, translations, locale }: Props) {
  const formatMoney = (n: number) => Math.round(n).toLocaleString(locale);
  const formatPercent = (n: number, digits: number) => n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });

  return (
    <div className="space-y-8">
      <div>
        <div className="text-sm font-medium text-gray-700 mb-2">{translations.mode.label}</div>
        <div
          role="group"
          aria-label={translations.mode.label}
          className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50"
        >
          {(['simple', 'advanced'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={values.mode === mode}
              onClick={() => onValuesChange('mode', mode)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                values.mode === mode
                  ? 'bg-white text-blue-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {translations.mode[mode]}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {values.mode === 'simple' ? translations.mode.simpleHelp : translations.mode.advancedHelp}
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">{translations.shared.title}</h3>
        <div className="space-y-6">
          <SliderField
            id="monthly-savings"
            label={translations.shared.monthlySavings}
            value={values.monthlySavings}
            min={100}
            max={20000}
            step={100}
            suffix={translations.currencySuffix}
            minLabel={`100 ${translations.currencySuffix}`}
            maxLabel={`20 000 ${translations.currencySuffix}`}
            onChange={(v) => onValuesChange('monthlySavings', v)}
            formatValue={formatMoney}
            inputMode="numeric"
          />
          <SliderField
            id="starting-amount"
            label={translations.shared.startingAmount}
            value={values.startingAmount}
            min={0}
            max={500000}
            step={1000}
            suffix={translations.currencySuffix}
            minLabel={`0 ${translations.currencySuffix}`}
            maxLabel={`500 000 ${translations.currencySuffix}`}
            onChange={(v) => onValuesChange('startingAmount', v)}
            formatValue={formatMoney}
            inputMode="numeric"
          />
          <SliderField
            id="savings-period"
            label={translations.shared.savingsPeriod}
            value={values.years}
            min={1}
            max={40}
            step={1}
            suffix={translations.yearsSuffix}
            minLabel={`1 ${translations.yearsSuffix}`}
            maxLabel={`40 ${translations.yearsSuffix}`}
            onChange={(v) => onValuesChange('years', v)}
            formatValue={(n) => String(Math.round(n))}
            inputMode="numeric"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4 space-y-5">
          <h3 className="text-base font-semibold text-blue-900">{translations.robotA}</h3>
          <SliderField
            id="robot-a-return"
            label={translations.expectedReturn}
            value={values.robotAReturn}
            min={0}
            max={15}
            step={0.1}
            suffix={translations.percentSuffix}
            minLabel="0%"
            maxLabel="15%"
            onChange={(v) => onValuesChange('robotAReturn', v)}
            formatValue={(n) => formatPercent(n, 1)}
          />
          <SliderField
            id="robot-a-fee"
            label={translations.fee}
            value={values.robotAFee}
            min={0}
            max={2.5}
            step={0.01}
            suffix={translations.percentSuffix}
            minLabel="0%"
            maxLabel="2,5%"
            onChange={(v) => onValuesChange('robotAFee', v)}
            formatValue={(n) => formatPercent(n, 2)}
          />
        </div>

        <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-4 space-y-5">
          <h3 className="text-base font-semibold text-amber-900">{translations.robotB}</h3>
          <SliderField
            id="robot-b-return"
            label={translations.expectedReturn}
            value={values.robotBReturn}
            min={0}
            max={15}
            step={0.1}
            suffix={translations.percentSuffix}
            minLabel="0%"
            maxLabel="15%"
            onChange={(v) => onValuesChange('robotBReturn', v)}
            formatValue={(n) => formatPercent(n, 1)}
          />
          <SliderField
            id="robot-b-fee"
            label={translations.fee}
            value={values.robotBFee}
            min={0}
            max={2.5}
            step={0.01}
            suffix={translations.percentSuffix}
            minLabel="0%"
            maxLabel="2,5%"
            onChange={(v) => onValuesChange('robotBFee', v)}
            formatValue={(n) => formatPercent(n, 2)}
          />
        </div>
      </div>
    </div>
  );
}
